<template>
	<section class="about" id="about">
		<div class="about__wrapper">
			<div class="about__container container">
				<h2 class="about__title heading h2">
					{{ $t('pages.main.sections.about.title') }}
				</h2>
				<div class="about__content">
					<div ref="mediaRef" class="about__media">
						<!-- Canvas for image sequence - только на десктопе (скрыт через CSS на мобилке) -->
						<canvas ref="canvasRef" class="about__canvas about__canvas--desktop"></canvas>

						<!-- Video - только на мобилке (скрыт через CSS на десктопе) -->
						<video
							v-if="isMounted && isMobile"
							id="video-about"
							ref="videoRef"
							src="https://dcdn.veonadx.com/landingveon/about-output.mp4"
							muted
							playsinline
							autoplay
							loop
							preload="metadata"
							poster="/images/sections/about/videoframe_0.webp"
							class="about__video about__video--mobile">
							<track kind="captions" srclang="en" label="English captions" src="/captions/empty.vtt" />
						</video>
					</div>
					<div class="about__info">
						<article class="about__article">
							<UIHeading level="3" class="about__article-title">
								{{ $t('pages.main.sections.about.article1.title') }}
							</UIHeading>
							<ul class="about__article-list">
								<li
									v-for="(item, i) in $tm('pages.main.sections.about.article1.list')"
									:key="i"
									class="about__article-item"
									v-html="$rt(item)"></li>
							</ul>
						</article>
						<article class="about__article">
							<UIHeading level="3" class="about__article-title">
								{{ $t('pages.main.sections.about.article2.title') }}
							</UIHeading>
							<ul class="about__article-list">
								<li
									v-for="(item, i) in $tm('pages.main.sections.about.article2.list')"
									:key="i"
									class="about__article-item"
									v-html="$rt(item)"></li>
							</ul>
						</article>
					</div>
				</div>
			</div>
		</div>
	</section>
</template>

<script setup lang="ts">
	const { $gsap: gsap } = useNuxtApp()
	const FRAME_COUNT = 791
	const BASE_FPS = 38
	const INITIAL_PRELOAD_COUNT = 24
	const PRELOAD_RANGE = 10
	const MAX_SCROLL_BOOST = 8
	const MOUSE_SHIFT_PX = 28
	const SCALE_INTENSITY = 0.07

	const videoRef = ref<HTMLVideoElement | null>(null)
	const mediaRef = ref<HTMLDivElement | null>(null)

	// Image sequence config using composable
	const {
		canvasRef,
		images,
		currentFrame,
		init: initImageSequence,
		drawFrame,
		preloadFrames,
	} = useImageSequence({
		frameCount: FRAME_COUNT,
		getFramePath: (index: number) => {
			const frameNumber = String(index + 1).padStart(4, '0')
			return `/images/freq/about/about_${frameNumber}.jpg`
		},
		triggerSelector: '.about',
		triggerStart: 'top bottom',
		triggerEnd: 'bottom top',
		initialPreloadCount: INITIAL_PRELOAD_COUNT,
		preloadRange: PRELOAD_RANGE,
	})

	let autoplayTicker: ((time: number, deltaTime: number, frame: number) => void) | null = null
	let scrollControlTween: gsap.core.Tween | null = null
	let frameFloat = 0
	let currentPlaybackSpeed = 1
	let targetPlaybackSpeed = 1
	let lastScrollDirection = 1
	let cleanupMouseParallax: (() => void) | null = null

	function updateTargetPlaybackSpeed(nextSpeed: number) {
		targetPlaybackSpeed = gsap.utils.clamp(-MAX_SCROLL_BOOST, MAX_SCROLL_BOOST, nextSpeed)
	}

	function startCanvasAutoplay() {
		if (autoplayTicker) return

		autoplayTicker = (_time: number, deltaTime: number) => {
			currentPlaybackSpeed += (targetPlaybackSpeed - currentPlaybackSpeed) * 0.12

			const frameStep = (deltaTime / 1000) * BASE_FPS * currentPlaybackSpeed
			frameFloat = (frameFloat + frameStep + FRAME_COUNT) % FRAME_COUNT

			const frameIndex = Math.floor(frameFloat)
			const frameImage = images.value[frameIndex]
			if (!frameImage) return

			if (!frameImage.src) {
				frameImage.src = `/images/freq/about/about_${String(frameIndex + 1).padStart(4, '0')}.jpg`
			}

			if (frameImage.complete && frameImage.naturalWidth > 0) {
				drawFrame(frameIndex)
				currentFrame.value = frameIndex
				void preloadFrames(frameIndex - PRELOAD_RANGE, frameIndex + PRELOAD_RANGE)
			}
		}

		gsap.ticker.add(autoplayTicker)
	}

	function setupScrollSpeedControl() {
		scrollControlTween?.kill()

		scrollControlTween = gsap.to(
			{},
			{
				scrollTrigger: {
					trigger: '.about',
					start: 'top bottom',
					end: 'bottom top',
					onUpdate: (self: { getVelocity: () => number; direction: number }) => {
						const velocity = Math.abs(self.getVelocity())
						if (velocity > 10) {
							lastScrollDirection = self.direction
						}
						const boost = gsap.utils.clamp(0, MAX_SCROLL_BOOST - 1, velocity / 320)
						updateTargetPlaybackSpeed(lastScrollDirection * (1 + boost))
					},
				},
			}
		)
	}

	function setupDesktopMouseParallax() {
		const mediaElement = mediaRef.value
		const parallaxTarget = canvasRef.value ?? mediaElement
		if (!parallaxTarget) return

		const moveX = gsap.quickTo(parallaxTarget, 'x', { duration: 0.45, ease: 'power3.out' })
		const moveY = gsap.quickTo(parallaxTarget, 'y', { duration: 0.45, ease: 'power3.out' })
		const scaleXTo = gsap.quickTo(parallaxTarget, 'scaleX', {
			duration: 0.45,
			ease: 'power3.out',
		})
		const scaleYTo = gsap.quickTo(parallaxTarget, 'scaleY', {
			duration: 0.45,
			ease: 'power3.out',
		})

		const handleMouseMove = (event: MouseEvent) => {
			const viewportWidth = window.innerWidth || 1
			const viewportHeight = window.innerHeight || 1
			const normalizedX = event.clientX / viewportWidth - 0.5
			const normalizedY = event.clientY / viewportHeight - 0.5
			const yRatio = event.clientY / viewportHeight
			const halfDistance = Math.abs(0.5 - yRatio) * 2
			const targetScaleRaw =
				yRatio <= 0.5 ? 1 + halfDistance * SCALE_INTENSITY : 1 - halfDistance * SCALE_INTENSITY
			const targetScale = gsap.utils.clamp(0.35, 1.85, targetScaleRaw)

			moveX(normalizedX * MOUSE_SHIFT_PX)
			moveY(normalizedY * MOUSE_SHIFT_PX)
			scaleXTo(targetScale)
			scaleYTo(targetScale)
		}

		const handleMouseLeave = () => {
			moveX(0)
			moveY(0)
			scaleXTo(1)
			scaleYTo(1)
		}

		window.addEventListener('mousemove', handleMouseMove)
		window.addEventListener('mouseleave', handleMouseLeave)

		cleanupMouseParallax = () => {
			window.removeEventListener('mousemove', handleMouseMove)
			window.removeEventListener('mouseleave', handleMouseLeave)
			moveX(0)
			moveY(0)
			scaleXTo(1)
			scaleYTo(1)
		}
	}

	function aboutAnimation() {
		gsap
			.timeline({
				scrollTrigger: {
					trigger: '.about',
					start: `${isMobile.value ? 'center' : 'top'} top`,
					end: 'bottom top',
					scrub: true,
					pin: true,
					pinSpacing: false,
				},
			})
			.to('.about__wrapper', {
				opacity: 0.5,
				yPercent: isMobile.value ? 0 : -20,
			})
	}

	// Анимация видео на мобилке
	function videoAnimationOnScroll() {
		gsap.to('.about__media', {
			scrollTrigger: {
				trigger: '.about',
				start: 'top top',
				end: 'bottom top',
				scrub: true,
			},
			yPercent: 100,
			scale: 2,
			opacity: 0.1,
		})
	}

	const isMobile = ref(false)
	const isMounted = ref(false)

	onMounted(async () => {
		// Определяем мобилку на клиенте
		isMobile.value = window.innerWidth < 1198.98
		isMounted.value = true

		aboutAnimation()

		// Canvas только на десктопе
		if (!isMobile.value) {
			await initImageSequence()
			setTimeout(() => {
				startCanvasAutoplay()
				setupScrollSpeedControl()
				setupDesktopMouseParallax()
			}, 100)
		} else {
			// Запускаем видео на мобилке
			await nextTick()
			if (videoRef.value) {
				videoRef.value.play().catch((e) => console.warn('Autoplay failed:', e))
			}
			setTimeout(() => {
				videoAnimationOnScroll()
			}, 100)
		}
	})

	onBeforeUnmount(() => {
		if (autoplayTicker) {
			gsap.ticker.remove(autoplayTicker)
			autoplayTicker = null
		}
		scrollControlTween?.kill()
		cleanupMouseParallax?.()
	})

	// Video ready hook commented out - using image sequence instead
	/*
	useVideoReady(
		'video-about',
		() => {
			setTimeout(() => {
				videoAnimationOnScroll()
			}, 500)
		},
		videoRef
	)
	*/
</script>

<style scoped lang="scss">
	.about {
		// scroll-margin-top: 80px;
		position: relative;
		overflow: clip;
		background-color: var(--black);
		will-change: transform;
		&__wrapper {
			padding: 50px 0 100px;
			background-color: var(--white);

			// will-change: transform;
			will-change: transform;
		}
		&__title {
			text-transform: uppercase;
			position: relative;
			z-index: 2;
		}
		&__media {
			position: relative;
			z-index: -1;
			width: 100%;
			max-width: 500px;
			height: 500px;

			@media (width <= 1198.98px) {
				position: sticky;
				top: 80px;
				max-width: 300px;
				height: 300px;
			}

			@media (width <= 1198.98px) {
				position: static;
			}

			// &.loaded {
			// 	.about__video {
			// 		opacity: 1;
			// 	}
			// 	.about__video-poster {
			// 		opacity: 0;
			// 	}
			// }
		}
		&__canvas {
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			object-fit: cover;
			&--desktop {
				display: block;

				@media (width <= 1198.98px) {
					display: none;
				}
			}
		}
		&__video {
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			object-fit: cover;
			&--mobile {
				display: none;

				@media (width <= 1198.98px) {
					display: block;
				}
			}
			&-poster {
				width: 100%;
				height: 100%;
			}
		}
		&__content {
			position: relative;
			z-index: 1;
			display: flex;
			gap: 48px;
			align-items: center;
			justify-content: space-evenly;

			@media (width <= 1198.98px) {
				align-items: flex-start;
			}

			@media (width <= 1198.98px) {
				flex-wrap: wrap;
			}
		}
		&__info {
			// display: flex;
			// align-items: flex-start;
			// gap: 48px;
			// @media (width <= 1198.98px) {
			// 	flex-wrap: wrap;
			// }
			display: grid;
			grid-template-columns: repeat(2, 1fr);
			gap: 48px;

			@media (width <= 1198.98px) {
				grid-template-columns: 1fr;
			}
		}
		&__article {
			display: flex;
			flex-direction: column;
			&-title {
				min-height: 4ch;
			}
			&-list {
				padding-left: 1.5em;
				margin-top: 16px;
				text-indent: -1.5em;
				list-style-type: disc;
			}
		}
	}
</style>
